import { PureComponent } from 'react';

// Material UI imports
import {
  Typography,
  ButtonGroup,
  Button,
  Avatar,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';

// Import lab components
import { Rating, Skeleton /* Rating */ } from '@material-ui/lab';

// React routing link
import { withRouter } from 'react-router-dom';

// Portlet items
import { Portlet, PortletContent, PortletFooter } from 'components';

// Name initials helper
import { getInitials } from 'helpers';

// DateTime helpers
import { DateTime } from 'luxon';

// Style
import styles from './styles';

class TutorBookingCard extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize the component's state
    const { loading, bookings } = props;
    this.state = {
      selected: !loading && bookings.length > 0 ? bookings[0] : null,
      showSubscriptionDialog: false,
      requiredSubscription: null
    };
    this.checkSubscriptionRequirement =
      this.checkSubscriptionRequirement.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { loading, bookings } = this.props;

    // Auto-select a new booking
    if (prevProps.bookings !== bookings) {
      this.setState({
        selected: !loading && bookings.length > 0 ? bookings[0] : null
      });
    }
  }
  checkSubscriptionRequirement() {
    const { selected } = this.state;
    const { student } = this.props;

    const subscription = student?.stripe?.subscription;
    const categories = subscription?.currentSubscription?.categories || [];
    // Check if free trial was activated OR if student has free trial tokens available
    // This allows students with refunded free trial tokens to attempt bookings
    // Tokens will be validated on the backend during actual booking
    const freeTrialActivated = student?.stripe?.freeTrialActivated === true;
    const hasFreeTrialTokens = (student?.data?.freeTrialToken || 0) > 0;
    // Allow booking if free trial was activated OR if they have refunded free trial tokens
    const canUseFreeTrial = freeTrialActivated || hasFreeTrialTokens;
    // Check if the subscription is active and has categories
    const hasValidSubscription =
      subscription && subscription.currentSubscription && categories.length > 0;

    const Plans = [
      // 'price_1R4HRwHns6zXPgc8A2KYJiq8',
      // 'price_1R4HKZHns6zXPgc866zuYJzK',
      'price_1Sg0VXHns6zXPgc8UdS7MI03', // Updated PTE price ID
      'price_1SptkrHns6zXPgc8mJODldvz' // Workplace price ID (AUD)
    ];
    const studentHasValidSubscription =
      hasValidSubscription && categories.some((f) => Plans.includes(f.priceId));

    // Check if student has tokens available for the subscription
    const hasPTESubscription = categories.some(
      (cat) => cat.priceId === 'price_1Sg0VXHns6zXPgc8UdS7MI03'
    );
    const hasWorkplaceSubscription = categories.some(
      (cat) => cat.priceId === 'price_1SptkrHns6zXPgc8mJODldvz'
    );

    // Allow booking if:
    // 1. Free trial is activated OR has free trial tokens (refunded tokens), OR
    // 2. Has valid subscription with matching priceId (tokens will be checked at booking time)
    if (canUseFreeTrial) return true;

    if (studentHasValidSubscription) {
      // Subscription exists with correct priceId, allow booking
      // Tokens will be validated at actual booking time
      return true;
    }

    // No valid subscription found
    this.setState({
      showSubscriptionDialog: true,
      requiredSubscription: {
        message: 'You need a valid subscription to book this tutor.'
      }
    });
    return false;
  }

  render() {
    const { classes, loading, bookings, tutor, onBook } = this.props;
    const { selected, showSubscriptionDialog, requiredSubscription } =
      this.state;
    const { image, name, isPte, rating } = bookings[0]?.profile.tutor;

    // Render the tutor booking card
    return (
      <>
        <Portlet className={classes.root}>
          <PortletContent className={classes.content}>
            {loading ? (
              <>
                <Skeleton variant="circle" className={classes.avatar} />
              </>
            ) : (
              <>
                <div className={'left_sec'}>
                  <Avatar src={image} alt={name} className={classes.avatar}>
                    <Typography variant="h3" style={{ color: 'white' }}>
                      {getInitials(name || '')}
                    </Typography>
                  </Avatar>
                  <Rating
                    readOnly
                    size="small"
                    precision={0.1}
                    value={!rating ? 0 : rating?.avg}
                    style={{ margin: '5px 0' }}
                  />
                  <Typography variant="h6" style={{ textAlign: 'center' }}>
                    {isPte ? 'PTE Workplace' : 'Workplace'}
                  </Typography>
                </div>

                <div className={classes.info}>
                  <Typography variant="h3">{name}</Typography>
                  <FormControl
                    variant="filled"
                    className={classes.date}
                    fullWidth>
                    <InputLabel
                      className={classes.subtitle}
                      htmlFor={`booking-date-${tutor}`}>
                      Time
                    </InputLabel>
                    <Select
                      value={selected}
                      onChange={(event) => {
                        this.setState({ selected: event.target.value });
                      }}
                      inputProps={{
                        name: 'age',
                        id: `booking-date-${tutor}`
                      }}>
                      {bookings.map((booking) => {
                        // Format the timestamp
                        const time = DateTime.fromMillis(booking.when);
                        const timeFormatted = time.toLocaleString(
                          DateTime.TIME_SIMPLE
                        );
                        const timeFormattedAmpm = time.toFormat('h:mm a');

                        // Render the menu item
                        return (
                          <MenuItem key={booking.when} value={booking}>
                            {timeFormatted} ({timeFormattedAmpm})
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>
              </>
            )}
          </PortletContent>
          <PortletFooter className={classes.buttonFill} noPadding>
            <ButtonGroup fullWidth variant="text">
              <Button
                disabled={loading}
                onClick={() => {
                  const { history } = this.props;
                  // console.log(tutor);
                  // console.log(bookings[0]?.tutor?.uid);
                  history.push(`/teacher/profile/${bookings[0]?.tutor}`);
                }}>
                view teacher
              </Button>
              <Button
                disabled={loading}
                color="primary"
                onClick={() => {
                  if (!this.checkSubscriptionRequirement()) return;
                  onBook(selected);
                }}>
                book
              </Button>
            </ButtonGroup>
          </PortletFooter>
        </Portlet>
        <Dialog
          open={showSubscriptionDialog}
          onClose={() => {
            this.setState({
              showSubscriptionDialog: false
            });
          }}
          aria-labelledby="subscription-dialog-title"
          maxWidth="sm"
          fullWidth>
          <DialogTitle id="subscription-dialog-title" sx={{ fontSize: '1rem' }}>
            <h2> Subscription Required</h2>
          </DialogTitle>
          <DialogContent>
            <Typography paragraph>
              To book this teacher, you need an active subscription.
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Unlock access to expert teachers by subscribing today! Ready to
              get started?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                this.setState({
                  showSubscriptionDialog: false
                });
              }}
              color="default">
              Cancel
            </Button>
            <Button
              onClick={() => {
                const { history } = this.props;
                history.push('/student/subscription');
              }}
              color="primary"
              variant="contained">
              Enroll Now
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default withRouter(withStyles(styles)(TutorBookingCard));
